// The doubt card (owner decision 2026-10-16): the owner answers what Loom
// was unsure about. Since 2026-10-29 an item a doubt names is filed at once
// as Loom's best guess (its held class; "Noch offen" in its body) when that
// is an issue class, and the card still asks; an item held as no issue
// waits on the card, unfiled. These are the card's rules, shared by the
// server (which answers, releases and applies) and the page (which offers
// the answers):
//
// - which answers a question offers (`doubtAnswersFor`) and which one is
//   Loom's own guess (`suggestedDoubtAnswer`);
// - what an answer makes of a held item not filed yet (`doubtReleaseOf`):
//   filed as a ticket or an epic, with which reason, or left unfiled;
// - what an answer does to a held item's filed issue (`answerEffectOf`), and
//   the reason it carries then (`answeredReasonOf`);
// - how many questions the card shows at once (`MEETING_MAX_DOUBTS`).
//
// Pure, browser-safe: no I/O, no regex lookbehind.

import {
  TICKET_REASONS,
  type AnswerEffect,
  type DoubtCode,
  type ItemClass,
  type ItemReason,
  type MeetingDoubtAnswer,
  type TicketClass,
  type TicketReason,
} from "./vocabulary.js";

/**
 * At most this many questions on the card at once: the triage eval's gate
 * ("doubts ≤ 5"). The rest wait under "Nicht übernommen" with
 * "Rückfrage offen" (and "Doch übernehmen") and move up as questions are
 * answered.
 */
export const MEETING_MAX_DOUBTS = 5;

/**
 * Which question a held item is asked when it has several doubts: one that
 * names two items first (its answer decides both), then the project, then
 * whether a floated idea is work now (its "only an idea" files nothing, so
 * it goes before the questions about what to file), then one item or
 * several, then a reference outside the transcript.
 */
export const DOUBT_ORDER: readonly DoubtCode[] = [
  "same_or_two",
  "which_project",
  "work_or_idea",
  "one_or_many",
  "unresolved_ref",
];

/**
 * The answers a question offers. `items`: how many held items it asks
 * about; "one topic or two?" can merge only a pair.
 */
export const doubtAnswersFor = (code: DoubtCode, items: number): readonly MeetingDoubtAnswer[] => {
  switch (code) {
    case "same_or_two":
      return items >= 2 ? ["as_held", "merged", "skip"] : ["as_held", "skip"];
    case "one_or_many":
    case "which_project":
    case "work_or_idea":
      return ["ticket", "epic", "skip"];
    case "unresolved_ref":
      return ["as_held", "skip"];
  }
};

const isIssueClass = (c: ItemClass | null): c is TicketClass => c === "ticket" || c === "epic";

/**
 * Loom's own guess, offered first: what it would have made of the items
 * without the doubt. None for "which project?" and "work now or only an
 * idea?": an unclear project, and whether anyone takes a floated idea on,
 * are exactly what Loom does not know.
 */
export const suggestedDoubtAnswer = (
  code: DoubtCode,
  heldClasses: readonly (ItemClass | null)[],
): MeetingDoubtAnswer | null => {
  if (code === "which_project" || code === "work_or_idea") return null;
  if (code === "one_or_many") {
    const first = heldClasses[0] ?? null;
    return isIssueClass(first) ? first : null;
  }
  return "as_held";
};

const isTicketReason = (r: ItemReason | null): r is TicketReason =>
  r !== null && (TICKET_REASONS as readonly string[]).includes(r);

/** The one issue a merged pair becomes: an epic when either would have been one. */
export const mergedClassOf = (heldClasses: readonly (ItemClass | null)[]): TicketClass =>
  heldClasses.includes("epic") ? "epic" : "ticket";

export interface DoubtRelease {
  readonly class: TicketClass;
  readonly reason: TicketReason;
}

/**
 * What an answer files for one held item, or null for nothing:
 * - `skip`: nothing;
 * - `as_held`: its held class with its held reason (a `none` item stays
 *   under "Nicht übernommen", with its held reason);
 * - `ticket` / `epic`: that class, with the held reason when the owner
 *   chose the class Loom held, else `promoted` (the owner decided it);
 * - `merged`: the pair's one issue (`mergedClassOf`), `promoted`.
 */
export const doubtReleaseOf = (
  answer: MeetingDoubtAnswer,
  held: { readonly heldClass: ItemClass | null; readonly heldReason: ItemReason | null },
  group: readonly (ItemClass | null)[] = [held.heldClass],
): DoubtRelease | null => {
  switch (answer) {
    case "skip":
      return null;
    case "as_held":
      return isIssueClass(held.heldClass)
        ? { class: held.heldClass, reason: isTicketReason(held.heldReason) ? held.heldReason : "promoted" }
        : null;
    case "ticket":
    case "epic":
      return {
        class: answer,
        reason: held.heldClass === answer && isTicketReason(held.heldReason) ? held.heldReason : "promoted",
      };
    case "merged":
      return { class: mergedClassOf(group), reason: "promoted" };
  }
};

/**
 * Does this answer file the item? An answered row that should have filed
 * and holds no ticket after a while is a release that died midway: the
 * card asks again.
 */
export const doubtAnswerFiles = (answer: MeetingDoubtAnswer, heldClass: ItemClass | null): boolean =>
  doubtReleaseOf(answer, { heldClass, heldReason: null }) !== null;

/**
 * The reason a held item carries once answered: `doubtReleaseOf`'s, except
 * that "work now" (`work_or_idea` answered `ticket` or `epic`) is never an
 * idea any more, whatever the held reason said: `promoted`. Null when the
 * answer files nothing.
 */
export const answeredReasonOf = (
  code: DoubtCode,
  answer: MeetingDoubtAnswer,
  held: { readonly heldClass: ItemClass | null; readonly heldReason: ItemReason | null },
  group: readonly (ItemClass | null)[] = [held.heldClass],
): TicketReason | null => {
  const release = doubtReleaseOf(answer, held, group);
  if (release === null) return null;
  if (code === "work_or_idea" && (answer === "ticket" || answer === "epic") && release.reason === "idea") {
    return "promoted";
  }
  return release.reason;
};

/**
 * What an answer does to a held item's issue, filed as `filedClass` before
 * the answer (owner decision 2026-10-29, "Full effect"):
 * - `skip` on "work now or only an idea?": it stays open as an idea epic
 *   (`reclass_idea`); any other `skip`: it is closed with a note (`close`);
 * - `as_held`: it stays as filed and loses its open point (`confirm`);
 * - `ticket` / `epic`: the filed class confirms it, the other switches it
 *   (`reclass_ticket`, `reclass_epic`);
 * - `merged` ("Ein Thema"): the `kept` item's issue takes in the other
 *   (`fold`), the `other` one is closed into it (`folded`); an item that is
 *   the `only` one of its question has no pair and is confirmed.
 */
export const answerEffectOf = (
  answer: MeetingDoubtAnswer,
  code: DoubtCode,
  filedClass: TicketClass,
  role: "only" | "kept" | "other",
): AnswerEffect => {
  switch (answer) {
    case "skip":
      return code === "work_or_idea" ? "reclass_idea" : "close";
    case "as_held":
      return "confirm";
    case "ticket":
    case "epic":
      return answer === filedClass ? "confirm" : answer === "epic" ? "reclass_epic" : "reclass_ticket";
    case "merged":
      return role === "kept" ? "fold" : role === "other" ? "folded" : "confirm";
  }
};
